import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Popover,
  PopoverContent,
  PopoverTrigger,
  VStack,
  HStack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { Checkbox } from 'src/components/atoms/Checkbox';

type MediaChannelsSelectProps = {
  options: string[];
  selectedChannels: Set<string>;
  onSelectionChange: (selected: Set<string>) => void;
};

export const MediaChannelsSelect: React.FC<MediaChannelsSelectProps> = ({
  options,
  selectedChannels,
  onSelectionChange,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(
    undefined,
  );

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase();
    return options.filter((channel) => channel.toLowerCase().includes(query));
  }, [options, searchQuery]);

  const handleToggle = (channel: string) => {
    const newSelected = new Set(selectedChannels);
    if (newSelected.has(channel)) {
      newSelected.delete(channel);
    } else {
      newSelected.add(channel);
    }
    onSelectionChange(newSelected);
  };

  useEffect(() => {
    if (triggerRef.current) {
      setTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [isOpen]);

  const displayValue =
    selectedChannels.size > 0 ? `${selectedChannels.size} selected` : '';

  return (
    <FormControl>
      <FormLabel
        fontFamily="marketingPage"
        fontSize="16px"
        fontWeight={500}
        color="content.title"
        mb={3}>
        Media Channels
      </FormLabel>
      <Popover isOpen={isOpen} onClose={onClose} onOpen={onOpen} isLazy>
        <PopoverTrigger>
          <InputGroup ref={triggerRef}>
            <Input
              placeholder="Select media channels"
              value={displayValue}
              readOnly
              cursor="pointer"
              onClick={onOpen}
              bg="surface.panel"
              border="1px solid"
              borderColor="surface.border"
              borderRadius="md"
              fontSize="16px"
              color="content.title"
              fontFamily="marketingPage"
              _placeholder={{ color: 'content.muted' }}
              _focus={{
                borderColor: 'icon.green',
                boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
              }}
            />
            <InputRightElement pointerEvents="none">
              <Icon
                iconName={isOpen ? 'chevron-up' : 'chevron-down'}
                boxSize={4}
                color="content.muted"
              />
            </InputRightElement>
          </InputGroup>
        </PopoverTrigger>
        <PopoverContent
          p={4}
          bg="surface.panel"
          borderRadius="md"
          boxShadow="lg"
          w={triggerWidth ? `${triggerWidth}px` : '100%'}>
          {/* Search Input */}
          <Box mb={3}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Icon iconName="search" boxSize={4} color="content.muted" />
              </InputLeftElement>
              <Input
                placeholder="Search channels"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                border="1px solid"
                borderColor="surface.border"
                borderRadius="md"
                pl={10}
                fontFamily="marketingPage"
                _placeholder={{ color: 'content.muted' }}
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}
              />
            </InputGroup>
          </Box>

          {/* Channels List */}
          <Box maxH="200px" overflowY="auto">
            <VStack align="stretch" spacing={1}>
              {filteredOptions.map((channel) => (
                <HStack
                  key={channel}
                  p={2}
                  borderRadius="md"
                  _hover={{ bg: 'sidebar.itemHoverBg' }}
                  cursor="pointer"
                  onClick={() => handleToggle(channel)}>
                  <Checkbox
                    isChecked={selectedChannels.has(channel)}
                    pointerEvents="none"
                  />
                  <Text fontSize="sm" fontFamily="marketingPage">
                    {channel}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </PopoverContent>
      </Popover>
    </FormControl>
  );
};
